import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { apiClient } from "../App";
import { Trophy, TrendingUp, Users, Medal, ChevronRight } from "lucide-react";

// StatSection component moved outside
const StatSection = ({ title, icon: Icon, data, colorClass, valueKey, labelKey, showPercentage, limit, showViewMore, onViewMore }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${colorClass}`} />
        <span className="font-semibold text-slate-700 text-sm">{title}</span>
      </div>
      {showViewMore && data.length > limit && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onViewMore}
          className="text-xs text-[#064E3B] hover:bg-[#064E3B]/10 h-7 px-2"
        >
          View All <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      )}
    </div>
    <div className="space-y-1">
      {data.slice(0, limit).map((item, index) => (
        <div
          key={item.user_id || item.names || index}
          className="flex items-center justify-between p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              index === 0 ? 'bg-yellow-400 text-yellow-900' :
              index === 1 ? 'bg-slate-300 text-slate-700' :
              index === 2 ? 'bg-amber-600 text-white' :
              'bg-slate-200 text-slate-600'
            }`}>
              {index + 1}
            </span>
            <span className="text-sm text-slate-700">{item[labelKey]}</span>
          </div>
          <span className="font-bold text-sm text-[#064E3B]">
            {showPercentage ? `${item[valueKey]}%` : item[valueKey]}
          </span>
        </div>
      ))}
    </div>
  </div>
);

const PlayerStatsWidget = ({ limit = 5, showViewMore = true }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await apiClient.get("/player-stats");
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch player stats", error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type) => {
    setModalType(type);
    setShowModal(true);
  };

  if (loading) {
    return (
      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-slate-200 rounded w-1/3"></div>
            <div className="h-8 bg-slate-200 rounded"></div>
            <div className="h-8 bg-slate-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats || stats.total_matches === 0) {
    return (
      <Card className="rounded-2xl border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <Trophy className="w-5 h-5 text-[#064E3B]" />
            Player Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-slate-500 text-center py-4">No matches recorded yet. Start recording scores to see player statistics!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="rounded-2xl border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading flex items-center gap-2 text-lg">
            <Trophy className="w-5 h-5 text-[#064E3B]" />
            Player Statistics
            <span className="text-xs font-normal text-slate-500 ml-auto">
              {stats.total_matches} matches played
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Top Winners */}
            <StatSection
              title="Most Wins"
              icon={Trophy}
              data={stats.top_winners}
              colorClass="text-yellow-500"
              valueKey="wins"
              labelKey="name"
              limit={limit}
              showViewMore={showViewMore}
              onViewMore={() => openModal("wins")}
            />

            {/* Best Win % */}
            <StatSection
              title="Best Win Rate"
              icon={TrendingUp}
              data={stats.top_win_percentage}
              colorClass="text-blue-500"
              valueKey="win_pct"
              labelKey="name"
              showPercentage
              limit={limit}
              showViewMore={showViewMore}
              onViewMore={() => openModal("win_pct")}
            />

            {/* Best Pairs */}
            <StatSection
              title="Top Pairs"
              icon={Users}
              data={stats.best_pairs}
              colorClass="text-green-500"
              valueKey="wins"
              labelKey="names"
              limit={limit}
              showViewMore={showViewMore}
              onViewMore={() => openModal("pairs")}
            />
          </div>
        </CardContent>
      </Card>

      {/* View All Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {modalType === "wins" && "All Players by Wins"}
              {modalType === "win_pct" && "All Players by Win Rate"}
              {modalType === "pairs" && "All Pairs by Wins"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {modalType === "wins" && stats.top_winners.map((item, index) => (
              <div key={item.user_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-yellow-400 text-yellow-900' :
                    index === 1 ? 'bg-slate-300 text-slate-700' :
                    index === 2 ? 'bg-amber-600 text-white' :
                    'bg-slate-200 text-slate-600'
                  }`}>
                    {index + 1}
                  </span>
                  <div>
                    <p className="font-medium text-slate-800">{item.name}</p>
                    <p className="text-xs text-slate-500">{item.matches} matches, {item.win_pct}% win rate</p>
                  </div>
                </div>
                <span className="font-bold text-lg text-[#064E3B]">{item.wins}</span>
              </div>
            ))}
            {modalType === "win_pct" && stats.top_win_percentage.map((item, index) => (
              <div key={item.user_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-yellow-400 text-yellow-900' :
                    index === 1 ? 'bg-slate-300 text-slate-700' :
                    index === 2 ? 'bg-amber-600 text-white' :
                    'bg-slate-200 text-slate-600'
                  }`}>
                    {index + 1}
                  </span>
                  <div>
                    <p className="font-medium text-slate-800">{item.name}</p>
                    <p className="text-xs text-slate-500">{item.wins}W / {item.losses}L ({item.matches} matches)</p>
                  </div>
                </div>
                <span className="font-bold text-lg text-[#064E3B]">{item.win_pct}%</span>
              </div>
            ))}
            {modalType === "pairs" && stats.best_pairs.map((item, index) => (
              <div key={item.names} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                <div className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-yellow-400 text-yellow-900' :
                    index === 1 ? 'bg-slate-300 text-slate-700' :
                    index === 2 ? 'bg-amber-600 text-white' :
                    'bg-slate-200 text-slate-600'
                  }`}>
                    {index + 1}
                  </span>
                  <div>
                    <p className="font-medium text-slate-800">{item.names}</p>
                    <p className="text-xs text-slate-500">{item.matches} matches, {item.win_pct}% win rate</p>
                  </div>
                </div>
                <span className="font-bold text-lg text-[#064E3B]">{item.wins}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PlayerStatsWidget;
